import React from 'react';
import { Link } from 'react-router-dom';

const SMSTermsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="sms-terms-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-mimo-primary to-mimo-accent text-white py-20">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-4">
            SMS Terms & Conditions
          </h1>
          <p className="text-xl text-white/90">
            Last Updated: July 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-slate max-w-none">

            {/* SMS Program Summary — highlighted disclosure */}
            <div className="bg-mimo-primary/5 border-2 border-mimo-primary rounded-xl p-6 md:p-8 mt-8 mb-10 not-prose" data-testid="sms-program-summary">
              <h2 className="text-2xl font-bold font-heading text-mimo-primary mb-6">
                SMS Program Summary
              </h2>
              <div className="space-y-5 text-slate-700">
                <div>
                  <p className="font-semibold text-slate-900">Brand:</p>
                  <p>mimoIT Technology Solutions</p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Types of Messages:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-1">
                    <li>IT service updates</li>
                    <li>Support ticket updates</li>
                    <li>Appointment scheduling</li>
                    <li>Security notifications</li>
                    <li>Customer support communications</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Message Frequency:</p>
                  <p>Message frequency varies.</p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Message & Data Rates:</p>
                  <p>Message and data rates may apply.</p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Customer Care:</p>
                  <p>Reply <strong>HELP</strong> for assistance or contact:</p>
                  <ul className="list-none pl-0 space-y-1 mt-1">
                    <li>
                      <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent underline">info@mimoit.com</a>
                    </li>
                    <li>
                      <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent underline">(201) 473-4443</a>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Opt-Out:</p>
                  <p>
                    Reply <strong>STOP</strong>, <strong>END</strong>, <strong>CANCEL</strong>, <strong>UNSUBSCRIBE</strong>, <strong>QUIT</strong>, <strong>REVOKE</strong>, or <strong>OPT OUT</strong> to any message.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-slate-900">Privacy Policy:</p>
                  <p>
                    <a href="https://mimoit.com/privacy" className="text-mimo-primary hover:text-mimo-accent underline break-all">https://mimoit.com/privacy</a>
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">SMS Program Overview</h2>
            <p className="text-slate-600 mb-4">
              By providing your mobile telephone number and opting in to receive text messages from mimoIT Technology Solutions ("mimoIT," "we," "our," or "us"), you consent to receive conversational SMS messages regarding your business relationship with mimoIT.
            </p>
            <p className="text-slate-600 mb-4">
              This SMS service is intended exclusively for person-to-person business communications between mimoIT employees and customers. It is not intended for marketing campaigns, mass messaging, or automated promotional communications.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Types of Messages</h2>
            <p className="text-slate-600 mb-4">You may receive SMS messages regarding:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>IT service updates</li>
              <li>Appointment scheduling and confirmations</li>
              <li>Support ticket updates</li>
              <li>Security notifications</li>
              <li>Planned maintenance notifications</li>
              <li>Responses to your questions</li>
              <li>General customer support communications</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Message Frequency</h2>
            <p className="text-slate-600 mb-4">
              Message frequency varies based upon your interactions with mimoIT and your service requests.
            </p>
            <p className="text-slate-600 mb-4">
              Most customers receive fewer than five (5) SMS messages per month.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Message & Data Rates</h2>
            <p className="text-slate-600 mb-4">
              Message and data rates may apply depending on your wireless carrier and mobile service plan.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">How to Opt In</h2>
            <p className="text-slate-600 mb-4">
              Customers may opt in by voluntarily providing their mobile telephone number during:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>A service request</li>
              <li>Website contact form</li>
              <li>Support ticket</li>
              <li>Telephone conversation</li>
              <li>Email correspondence</li>
              <li>Other direct business communication</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">How to Opt Out</h2>
            <p className="text-slate-600 mb-4">
              Reply <strong>STOP</strong> at any time to discontinue SMS communications from that mimoIT phone number.
            </p>
            <p className="text-slate-600 mb-4">You may also reply:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>STOP</li>
              <li>END</li>
              <li>CANCEL</li>
              <li>UNSUBSCRIBE</li>
              <li>QUIT</li>
              <li>REVOKE</li>
              <li>OPT OUT</li>
            </ul>
            <p className="text-slate-600 mb-4">
              After opting out, you may receive one confirmation message. No additional SMS messages will be sent unless you subsequently opt back in.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Help</h2>
            <p className="text-slate-600 mb-4">
              Reply <strong>HELP</strong> for assistance.
            </p>
            <p className="text-slate-600 mb-4">You may also contact us:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Email: <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a></li>
              <li>Phone: <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent">(201) 473-4443</a></li>
              <li>Website: <a href="https://mimoit.com/contact" className="text-mimo-primary hover:text-mimo-accent">https://mimoit.com/contact</a></li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Privacy</h2>
            <p className="text-slate-600 mb-4">
              Your privacy is important to us.
            </p>
            <p className="text-slate-600 mb-4">
              Mobile opt-in data, SMS consent records, and mobile telephone numbers collected for SMS communications will <strong>NOT</strong> be sold, rented, shared, transferred, or disclosed to third parties or affiliates for marketing or promotional purposes.
            </p>
            <p className="text-slate-600 mb-4">
              Information may be shared only with trusted communications service providers solely for the purpose of delivering SMS messages to you. These providers are contractually prohibited from using your information for any other purpose.
            </p>
            <p className="text-slate-600 mb-4">
              Our complete Privacy Policy is available at:{' '}
              <a href="https://mimoit.com/privacy" className="text-mimo-primary hover:text-mimo-accent">https://mimoit.com/privacy</a>
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Supported Carriers</h2>
            <p className="text-slate-600 mb-4">
              Delivery is subject to your wireless carrier's network availability.
            </p>
            <p className="text-slate-600 mb-4">
              Carriers are not responsible for delayed or undelivered messages.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Changes</h2>
            <p className="text-slate-600 mb-4">
              We may update these SMS Terms from time to time. Changes become effective upon posting this page.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Contact Information</h2>
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <p className="text-slate-900 font-semibold mb-2">mimoIT Technology Solutions</p>
              <p className="text-slate-600">Email: <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a></p>
              <p className="text-slate-600">Phone: <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent">(201) 473-4443</a></p>
              <p className="text-slate-600">Web: <a href="https://mimoit.com/contact" className="text-mimo-primary hover:text-mimo-accent">https://mimoit.com/contact</a></p>
            </div>

            <div className="bg-mimo-primary/5 border-l-4 border-mimo-primary rounded p-6 mt-8">
              <p className="text-slate-700">
                Message & data rates may apply. Reply <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for assistance.
              </p>
            </div>

          </div>

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Return to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SMSTermsPage;
